import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Modal } from 'react-bootstrap';
import { userService } from '../_services/user.service';
import { readAccounts, readPreference } from '../_services/savedAccounts';
import { authenticatedIdentity, readSession } from '../_services/webSession';
import { SavePreferenceOptions } from './AccountPicker';
import DatacoLoadingIndicator from './DatacoLoadingIndicator';

function replacementRequired(accounts, user, selectedMode) {
    return selectedMode !== 'none'
        && accounts.length >= 5 && !accounts.some(account => account.id === String(user?.id));
}

export default function AccountLogoutFlow({ action, onComplete, onCancel }) {
    const [user] = useState(readSession);
    const [mode, setMode] = useState(readPreference);
    const [replacementId, setReplacementId] = useState('');
    const [choosingReplacement, setChoosingReplacement] = useState(false);
    // With a saved preference and no replacement needed, logout starts immediately:
    // begin busy so the dialog never flashes before the loading indicator.
    const [busy, setBusy] = useState(() => {
        const preference = readPreference();
        return Boolean(preference) && !replacementRequired(readAccounts(), readSession(), preference);
    });
    const [error, setError] = useState('');
    const started = useRef(false);
    const inFlight = useRef(false);
    const accounts = readAccounts();
    const needsReplacement = selectedMode => replacementRequired(accounts, user, selectedMode);

    async function complete(selectedMode, selectedReplacement, withoutSaving = false) {
        if (inFlight.current) return;
        if (!user || authenticatedIdentity() !== action.originIdentity) {
            setBusy(false);
            setError('The account changed. Please try again.');
            return;
        }

        inFlight.current = true;
        setBusy(true);
        setError('');
        try {
            let saved;
            if (!withoutSaving) {
                await userService.changeSavePreference(selectedMode);
                saved = await userService.saveCurrentAccount(user, selectedMode, selectedReplacement, action.originIdentity);
            }
            if (authenticatedIdentity() !== action.originIdentity) throw new Error('The account changed. Please try again.');

            await userService.logout(action.originIdentity);
            // Stay busy: the login page replaces this flow, so the dialog must not reappear.
            onComplete(saved?.notice);
        } catch (failure) {
            setError(failure.message || 'The account could not be saved. Retry or log out without saving.');
            setBusy(false);
        } finally {
            inFlight.current = false;
        }
    }

    useEffect(() => {
        if (started.current) return;

        started.current = true;
        const preference = readPreference();
        if (!preference) return;
        if (needsReplacement(preference)) {
            setChoosingReplacement(true);
            return;
        }

        complete(preference);
    }, []);

    const continueLogout = () => {
        if (needsReplacement(mode) && !replacementId) {
            setChoosingReplacement(true);
            return;
        }

        complete(mode, replacementId);
    };

    // While saving and logging out, show only the Dataco loading indicator; errors reopen the dialog.
    if (busy) {
        return <div className="account-logout-loading">
            <DatacoLoadingIndicator label="Saving your choice and logging out" />
        </div>;
    }

    return <Modal show onHide={onCancel} centered backdrop="static">
        <Modal.Header closeButton>
            <Modal.Title>{choosingReplacement ? 'Choose an account to replace' : 'Save accounts on this browser?'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            {error && <Alert variant="danger" role="alert">{error}</Alert>}
            {choosingReplacement ? <>
                <p>You have five saved accounts. Choose one to replace with <strong>{user?.username}</strong>.</p>
                <fieldset className="account-save-options">
                    <legend className="visually-hidden">Account to replace</legend>
                    {accounts.map(account => <label className="account-save-option" key={account.id}>
                        <input type="radio" name="replacement-account" checked={replacementId === account.id} onChange={() => setReplacementId(account.id)} />
                        <span>{account.username}{account.companyName ? ` · ${account.companyName}` : ''}</span>
                    </label>)}
                </fieldset>
            </> : <>
                <p>This preference applies to all accounts on this browser. You can change it from Saved accounts.</p>
                <SavePreferenceOptions value={mode} onChange={setMode} />
            </>}
        </Modal.Body>
        <Modal.Footer>
            <Button variant="outline-secondary" onClick={onCancel}>Cancel</Button>
            {(choosingReplacement || error) && <Button variant="outline-secondary" onClick={() => complete(mode, null, true)}>Log out without saving</Button>}
            <Button disabled={!mode || (choosingReplacement && !replacementId)} onClick={continueLogout}>
                {choosingReplacement ? 'Replace and log out' : 'Continue'}
            </Button>
        </Modal.Footer>
    </Modal>;
}
